import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Quotes, Heart, Sparkle, ArrowRight, CalendarCheck } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CALENDLY_URL, CONSULT_PRICE } from "@/lib/constants";
import api from "@/lib/api";

const HERO_IMG = `${process.env.PUBLIC_URL || ""}/avnni.jpg`;
const MID_IMG = "https://images.unsplash.com/photo-1557316655-8715fdecd2d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const MAYBES = [
  "Maybe you've become the person everyone depends on — but you don't remember the last time someone truly asked how you were.",
  "Maybe your mind never stops. You overthink every conversation, replay every mistake, and somehow still feel like you're not doing enough.",
  "Maybe you've achieved everything you thought would make you happy, yet there's a quiet emptiness you can't explain.",
  "Or maybe you're simply tired.",
];

const TIRED = ["Tired of pretending you're okay.", "Tired of holding everything together.", "Tired of searching for answers while feeling further away from yourself."];

const SAW = [
  "People carrying anxiety behind successful careers.",
  "People losing themselves in relationships.",
  "People trapped in cycles of self-doubt, people-pleasing, burnout, grief, or emotional pain they didn't know how to name.",
];

const TRUTHS = ["You are not too much.", "You are not too broken.", "You are not too late."];

function bookConsult() {
  api.post("/events", { event: "paid_cta_clicked", meta: { source: "about-avnni" } }).catch(() => {});
  window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
}

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

export default function About() {
  return (
    <div className="min-h-screen bg-slate-950" data-testid="about-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40">
        <div className="absolute -right-24 top-10 h-72 w-72 rounded-full bg-teal-500/10 blur-3xl" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
          <motion.div variants={reveal} initial="hidden" animate="show">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-500">Meet your guide</p>
            <h1 className="mt-4 font-heading text-4xl font-light leading-[1.05] tracking-tight text-slate-50 sm:text-6xl">
              I'm <span className="text-teal-300">Avnni</span>.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-400">
              What if you've been carrying something for so long that you've forgotten what it feels like to put it down?
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button onClick={bookConsult} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-semibold text-slate-950 transition-all hover:bg-amber-400" data-testid="about-hero-book-btn">
                <CalendarCheck size={16} weight="fill" /> Book a 1:1 session — {CONSULT_PRICE}
              </button>
              <span className="text-sm text-slate-500">12+ years · thousands of conversations</span>
            </div>
          </motion.div>

          <motion.div variants={reveal} initial="hidden" animate="show" custom={1} className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-slate-800">
              <img src={HERO_IMG} alt="Avnni" className="h-[460px] w-full object-cover object-top sm:h-[540px]" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="font-heading text-xl font-medium text-slate-50">Avnni</p>
                <p className="text-sm text-teal-300">Therapeutic guide · Depth & behavioural work</p>
              </div>
            </div>
            <p className="mt-2 text-center text-[11px] text-slate-600">A safe space where your mind, emotions and soul are all welcome.</p>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        {/* The "maybe" sequence */}
        <section className="space-y-6 border-t border-slate-800/60 py-16">
          {MAYBES.map((m, i) => (
            <motion.p key={i} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={i}
              className={`leading-relaxed ${i === MAYBES.length - 1 ? "font-heading text-2xl font-light text-slate-100" : "text-lg text-slate-400"}`}>
              {m}
            </motion.p>
          ))}

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-1 pt-4">
            {TIRED.map((t) => <p key={t} className="font-heading text-xl font-light text-slate-300">{t}</p>)}
          </motion.div>

          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="pt-6">
            <p className="text-lg text-slate-400">If any part of that feels familiar, I want you to know something.</p>
            <p className="mt-4 font-heading text-3xl font-light text-teal-300 sm:text-4xl">You're not alone.</p>
            <p className="mt-1 font-heading text-3xl font-light text-teal-300 sm:text-4xl">And you're not broken.</p>
          </motion.div>
        </section>

        {/* Her story */}
        <section className="border-t border-slate-800/60 py-16">
          <div className="flex items-center gap-2 text-teal-500"><Sparkle size={16} weight="fill" /><p className="text-xs font-bold uppercase tracking-[0.2em]">My story</p></div>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-300">
            <p>For over <span className="text-slate-100">12 years</span>, I've worked closely with people from every walk of life — leaders, entrepreneurs, professionals, parents, and young adults. Different stories, different lives, but the same silent struggle beneath the surface.</p>
            <ul className="space-y-2.5 border-l-2 border-slate-800 pl-5">
              {SAW.map((s) => <li key={s} className="text-slate-400">{s}</li>)}
            </ul>
            <p>The more people I met, the more I realised something profound:</p>
          </div>

          <motion.blockquote variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="relative my-10 rounded-2xl border border-teal-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-8">
            <Quotes size={28} weight="fill" className="text-teal-500/40" />
            <p className="mt-3 font-heading text-2xl font-light leading-snug text-slate-100">
              Most of us don't need someone to fix us. We need someone who can truly <span className="text-teal-300">see</span> us.
            </p>
          </motion.blockquote>

          <div className="space-y-5 text-base leading-relaxed text-slate-300">
            <p>That understanding led me beyond conventional answers. Into psychology. Into human behaviour. Into spiritual wisdom — the space where science meets self-awareness, and where lasting healing begins.</p>
            <p>The work I do today isn't about giving you advice or telling you who you should become. It's about helping you remember who you were <span className="text-slate-100">before</span> fear, trauma, expectations, heartbreak, and survival taught you to become someone else.</p>
          </div>
        </section>

        {/* The space / approach */}
        <section className="relative my-4 overflow-hidden rounded-3xl border border-slate-800">
          <img src={MID_IMG} alt="Coming home to yourself" className="h-64 w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-slate-950/70" />
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
            <Heart size={24} weight="duotone" className="text-teal-300" />
            <p className="mt-3 max-w-lg font-heading text-xl font-light text-slate-100">Our conversations are honest. There's no pressure to perform. No version of yourself you need to pretend to be.</p>
          </div>
        </section>

        {/* Closing truths */}
        <section className="border-t border-slate-800/60 py-16 text-center">
          <p className="text-base text-slate-400">After thousands of conversations, one truth has stayed with me:</p>
          <div className="mt-6 space-y-2">
            {TRUTHS.map((t, i) => (
              <motion.p key={t} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="font-heading text-3xl font-light text-slate-100 sm:text-4xl">{t}</motion.p>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-slate-400">
            You've simply been carrying more than anyone should have to carry alone. You don't have to have everything figured out before reaching out. You just have to begin.
          </p>
        </section>
      </div>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-5 pb-24 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-amber-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-8 text-center sm:p-12" data-testid="about-final-cta">
          <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-400">
              <CalendarCheck size={13} weight="fill" /> 1:1 with Avnni
            </span>
            <div className="mt-6 flex justify-center">
              <img src={HERO_IMG} alt="Avnni" className="h-20 w-20 rounded-full border-2 border-amber-500/30 object-cover object-top" />
            </div>
            <h2 className="mt-5 font-heading text-3xl font-light text-slate-50">And when you're ready, I'd be honoured to walk beside you.</h2>
            <p className="mx-auto mt-3 max-w-md text-sm text-slate-400">A private, 60-minute session held over Zoom — a safe space to be honestly, fully yourself.</p>
            <button onClick={bookConsult} className="mt-7 inline-flex items-center gap-2 rounded-full bg-amber-500 px-8 py-4 text-sm font-semibold text-slate-950 transition-all hover:bg-amber-400" data-testid="about-book-btn">
              Book your session — {CONSULT_PRICE} <ArrowRight size={16} weight="bold" />
            </button>
            <p className="mt-5 text-xs text-slate-500">Not sure where to begin? <Link to="/#all" className="text-teal-400 hover:text-teal-300">Take a free assessment first →</Link></p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
